import React, { ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import * as Api from "../../../api";

import { CombinedState } from "../../../redux/store/state";
import { NavigationState } from "../../../redux/slices/navigationSlice";

import { getFeatureConfigurationIsEnabled } from "../../../helper/feature-configuration-helper";

import Routes from "../router/routes";
import InternalSidebar from "../sidebar/internal-sidebar";
import UserFeedbackModal from "../components/user-feedback/user-feedback-modal";
import Header from "../header/internal-header";
import Footer from "../footer/footer";
import { AutomationQAUsers, Container, REACT_APP_GOOGLE_ANALYTICS_TAG_ID } from "../../../constants";
import store from "../../../redux/store";
import { isSuperAdmin } from "../../../helper/role-helper";
import { toLiteral } from "../../../helper/locale-utils";
import MaintenanceMode from "../../recruitment/processes/components/maintenance-mode";
import Notification, { NotificationHeader } from "@adeccoux/tag-ds/notification";
import RecruiterCopilot from "../components/recruiter-copilot/recruiter-copilot";
import { insertGoogleTag } from "../cookiesAndTracking/googleAnalytics";

/**
 * Component that renders the internal page
 */
const InternalContainer: React.FC<any> = (props): ReactElement => {
  const navigationState: NavigationState = useSelector((state: CombinedState) => state.navigation);

  const currentRoute = navigationState.currentRoute;
  const userFeedbackFC = getFeatureConfigurationIsEnabled("General Settings", "user-feedback");

  const [sidebar, setSidebar] = useState(false);
  const [showHeader, setShowHeader] = useState(false);
  const [userFeedback, setUserFeedback] = useState(false);
  const [userFeedbackClosed, setUserFeedbackClosed] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);

  const isMaintenanceMode = getFeatureConfigurationIsEnabled("General Settings", "recruiter-maintenance-mode");
  const tenantId = store.getState().tenant?.selectedTenant?.id;

  const recruiterCopilot = getFeatureConfigurationIsEnabled("Generative AI", "copilot-bubble");

  // if visitor is comming using external token we hide feedback modal
  const external = store?.getState()?.auth?.external || null;

  // User email loged in
  const userEmail = store?.getState()?.tenant?.userEmail ?? "";

  useEffect(() => {
    setSidebar(currentRoute?.id !== "LOGIN" && currentRoute?.id !== "LOGIN_TOKEN");
    setShowHeader(currentRoute?.id !== "LOGIN" && currentRoute?.id !== "LOGIN_TOKEN");
    if (currentRoute?.id !== "LOGIN" && currentRoute?.id !== "LOGIN_TOKEN" && userFeedbackFC && !userFeedbackClosed && !external && !AutomationQAUsers?.includes(userEmail)) fetchUserFeedback();
    else setUserFeedback(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute, userFeedbackFC]);

  useEffect(() => insertGoogleTag(REACT_APP_GOOGLE_ANALYTICS_TAG_ID), []);

  useEffect(() => {
    setShowMaintenance(isMaintenanceMode && isSuperAdmin());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, isMaintenanceMode]);

  async function fetchUserFeedback() {
    try {
      const userFeedbackResponse = await Api.getUserFeedback();
      if (!userFeedbackResponse) setUserFeedback(true);
    } catch (error) {
      if ((error as any)?.message === "EntityNotFoundError") setUserFeedback(true);
    }
  }

  function closeFeedback(close: boolean) {
    setUserFeedback(close);
    setUserFeedbackClosed(true);
  }

  return (
    <div id="internal-container">
      {sidebar && !currentRoute?.fullSizeView && <InternalSidebar />}
      <div id="app-content" className={external ? "no-sidebar" : ""}>
        {showMaintenance && (
          <Notification renderAsPortal={true} data-testid="notification" show warning>
            <NotificationHeader>{toLiteral({ id: "You are under maintenance mode. Remember to turn it off when the maintenance has finished." })}</NotificationHeader>
          </Notification>
        )}
        {showHeader && !currentRoute?.fullSizeView && <Header />}
        {isMaintenanceMode && !isSuperAdmin() ? <MaintenanceMode /> : <Routes onShowSnackbar={props.onShowSnackbar} getAppInsights={props.getAppInsights} />}
        <Footer container={Container.internal} />
      </div>
      {userFeedback && <UserFeedbackModal userFeedback={userFeedback} setUserFeedback={closeFeedback} />}
      {recruiterCopilot && <RecruiterCopilot />}
    </div>
  );
};

export default InternalContainer;
